import axios from 'axios';
import { notification } from 'antd';
import { api_url } from '../configs/app-global';
import { store } from '../redux/store';
import { clearUser } from '../redux/slices/auth';
import i18n from '../configs/i18next';
import { toast } from 'react-toastify';

const service = axios.create({
  baseURL: api_url,
  timeout: 60000, // 60s for slow/cold-start backends (e.g. Render)
});

// Config
const TOKEN_PAYLOAD_KEY = 'authorization';
const AUTH_TOKEN = 'token';
const AUTH_TOKEN_TYPE = 'Bearer';

// API Request interceptor
service.interceptors.request.use(
  (config) => {
    const access_token = localStorage.getItem(AUTH_TOKEN);

    if (access_token) {
      config.headers[TOKEN_PAYLOAD_KEY] = AUTH_TOKEN_TYPE + ' ' + access_token;
    }
    if (config.method === 'get') {
      config.params = { lang: i18n.language, ...config.params };
    }

    return config;
  },
  (error) => {
    // Do something with request error here
    notification.error({
      message: 'Error',
    });
    Promise.reject(error);
  }
);

// API respone interceptor
service.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    const currentPath =
      typeof window !== 'undefined' ? window.location?.pathname || '' : '';
    const isLoginScreen = currentPath === '/login';
    const isLoginRequest =
      error.config?.url?.includes('auth/login') ||
      (error.config?.baseURL && error.config?.url && `${error.config.baseURL}${error.config.url}`.includes('auth/login'));
    const isLogoutRequest = error.config?.url?.includes('auth/logout');

    // Login page shows its own error; skip global toast to avoid 2 notifications
    if (isLoginRequest || isLoginScreen || isLogoutRequest) {
      return Promise.reject(error);
    }

    /*
     * Background polls fail quietly.
     *
     * Requests nobody asked for — the badge counters on their timers — used to
     * raise the same toast as a button the admin had just pressed. When the
     * messages poll started failing, that put "8 RESOURCE_EXHAUSTED: Quota
     * exceeded" on screen every twenty seconds, on whatever page the shop
     * happened to be working on. A caller passes `silent: true` to opt out;
     * the promise still rejects, so the caller can still decide what to do.
     *
     * `silent` may also be a list of statuses — `silent: [404]` — for a caller
     * that handles one answer itself (a product page showing "this product no
     * longer exists") but still wants the usual toast for everything else; or a
     * function of the error, for a caller that reports every failure on the
     * page but still wants a 401 to log out as usual.
     */
    const silent = error.config?.silent;
    if (
      silent === true ||
      (Array.isArray(silent) && silent.includes(error.response?.status)) ||
      (typeof silent === 'function' && silent(error))
    ) {
      return Promise.reject(error);
    }

    let notificationParam = {
      message: error.response?.data?.message
        ? i18n.t(error.response.data.message)
        : (i18n.t('error') || 'Something went wrong'),
    };

    const hasNoHttpResponse = !error.response;
    const isOffline =
      typeof navigator !== 'undefined' && navigator.onLine === false;
    const isNetworkFailure =
      hasNoHttpResponse &&
      (error.code === 'ERR_NETWORK' ||
        error.message === 'Network Error' ||
        String(error.message || '').toLowerCase().includes('network'));

    if (isOffline || isNetworkFailure) {
      notificationParam.message = isOffline
        ? i18n.t('internet.offline') ||
          'No internet connection. Please check your connection and try again.'
        : i18n.t('network.slow_or_failed') ||
          'Network request failed. Please check your internet connection or try again on a faster network.';
    }

    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      notificationParam.message = i18n.t('request.timeout') || 'Request timed out. The server may be slow; please try again.';
    }

    if (error.response?.status === 502 || error.response?.status === 503) {
      notificationParam.message = i18n.t('server.unavailable') || 'Server temporarily unavailable. Please try again in a moment.';
    }

    if (error.response?.status === 504) {
      notificationParam.message = i18n.t('gateway.timeout') || 'The server took too long to respond. Please try again.';
    }

    if (error.response?.status === 508) {
      notificationParam.message = error.response?.data?.message;
    }

    if (error.response?.status === 500) {
      notificationParam.message = error.response?.data?.message || 'Internal server error';
    }

    const singleStoreMsg = error.response?.data?.message || '';
    if (singleStoreMsg.toLowerCase().includes('only one shop is allowed') || singleStoreMsg.includes('Single-store')) {
      notificationParam.message = i18n.t('settings.general')
        ? `${i18n.t('settings.general')}: only one store is allowed in single-store mode.`
        : 'Only one store is allowed in single-store mode.';
    }

    if (error.response?.data?.params) {
      if (Object.values(error.response?.data?.params)[0]) {
        notificationParam.message = Object.values(error.response?.data?.params)[0].at(0);
      }
    }

    // Only 401 = session expired. 403 is permission/business rule — do not log out (see USERS_AND_MESSAGES_ADMIN_INTEGRATION.md).
    if (error.response?.status === 401) {
      const hadToken = Boolean(localStorage.getItem(AUTH_TOKEN));
      localStorage.removeItem(AUTH_TOKEN);
      store.dispatch(clearUser());
      if (!hadToken) {
        return Promise.reject(error);
      }
      notificationParam.message =
        i18n.t('please.login.again') || 'Please login again';
    }

    const message = notificationParam.message || 'Request failed. Please try again.';
    toast.error(message, {
      toastId:
        isOffline || isNetworkFailure
          ? 'network-connectivity-error'
          : error.response?.status ?? error.config?.url,
    });
    return Promise.reject(error);
  }
);

export default service;
